import { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Label } from "../../components/ui/label";
import { fetchCancelReasonsByIfoodOrder, fetchCancelReasonsByPedido } from "./api";

/**
 * Lista de motivos de cancelamento do iFood para o pedido — obrigatória na
 * homologação ("consulte /cancellationReasons e exiba ao usuário"). A lista
 * muda com o momento do pedido, por isso é buscada na hora, nunca fixa.
 *
 * Passe `pedidoId` (id do Núcleo) OU `ifoodOrderId`. `onChange` recebe
 * `{ code, description }` ou null.
 */
export default function CancelReasonSelect({ pedidoId, ifoodOrderId, value, onChange }) {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    const load = pedidoId
      ? fetchCancelReasonsByPedido(pedidoId)
      : fetchCancelReasonsByIfoodOrder(ifoodOrderId);
    load
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data?.reasons) ? data.reasons : [];
        setReasons(list);
        if (list.length === 0) setError("O iFood não devolveu motivos para este pedido agora.");
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Não foi possível carregar os motivos do iFood.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [pedidoId, ifoodOrderId]);

  return (
    <div>
      <Label className="text-sm font-semibold">
        Motivo do cancelamento (lista do iFood) <span className="text-red-500">*</span>
      </Label>
      <p className="text-xs text-muted-foreground mt-0.5 mb-2">
        O cliente vê este motivo no app do iFood. Cancelamentos em excesso podem fechar a loja temporariamente.
      </p>
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Buscando motivos no iFood…
        </div>
      )}
      {!loading && error && (
        <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400 py-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {!loading && reasons.length > 0 && (
        <div className="max-h-56 overflow-y-auto rounded-lg border divide-y">
          {reasons.map((r) => {
            const selected = value?.code === r.code;
            return (
              <button
                key={r.code}
                type="button"
                onClick={() => onChange?.({ code: r.code, description: r.description })}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 transition-colors ${
                  selected ? "bg-red-50 dark:bg-red-950/30" : "hover:bg-muted/60"
                }`}
              >
                <span
                  className={`w-4 h-4 rounded-full border-2 shrink-0 ${
                    selected ? "border-red-600 bg-red-600" : "border-muted-foreground/40"
                  }`}
                />
                <span className="flex-1">{r.description}</span>
                <span className="font-mono text-[11px] text-muted-foreground">{r.code}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
